import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Key, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

export function AdminKeysTab({
  accessKeys, newKeyPlan, setNewKeyPlan, handleCreateKey, handleDeleteKey,
  influencerInvites, newInviteName, setNewInviteName, handleCreateInvite, handleDeleteInvite,
  virtualKeyPlan, setVirtualKeyPlan, handleGenerateVirtualKey, generatedVirtualKey
}) {
  return (
    <div className="space-y-4">
      <Card className="bg-white border border-slate-200">
        <CardHeader><CardTitle className="text-lg text-slate-900">Gerar Nova Chave de Acesso</CardTitle></CardHeader>
        <CardContent>
          <div className="flex items-center gap-3">
            <Select value={newKeyPlan} onValueChange={setNewKeyPlan}>
              <SelectTrigger className="w-48" data-testid="key-plan-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="premium">Premium</SelectItem>
                <SelectItem value="platinum">Platinum</SelectItem>
                <SelectItem value="black">Black</SelectItem>
              </SelectContent>
            </Select>
            <Button onClick={handleCreateKey} className="bg-slate-700 hover:bg-slate-800 text-white" data-testid="create-key-btn">Gerar Chave</Button>
          </div>
        </CardContent>
      </Card>

      <div className="space-y-2">
        {accessKeys.map(k => (
          <Card key={k.key} className={`border ${k.used ? 'bg-slate-50 border-slate-200' : 'bg-white border-slate-200'}`} data-testid={`key-${k.key}`}>
            <CardContent className="py-3">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div className="flex items-center gap-3">
                  <code className="font-mono text-lg font-bold text-slate-900 bg-slate-100 px-3 py-1 rounded">{k.key}</code>
                  <Badge className={`text-xs capitalize ${k.plan === 'black' ? 'bg-slate-900 text-white border-slate-800' : k.plan === 'platinum' ? 'bg-purple-100 text-purple-700 border-purple-200' : 'bg-blue-100 text-blue-700 border-blue-200'}`}>{k.plan}</Badge>
                  {k.used ? <Badge className="bg-red-100 text-red-600 border-red-200 text-xs">Utilizada</Badge> : <Badge className="bg-green-100 text-green-700 border-green-200 text-xs">Disponivel</Badge>}
                </div>
                <div className="flex items-center gap-2 text-xs text-slate-500">
                  {k.used && k.used_by && <span>Usada por: {k.used_by}</span>}
                  <span>{new Date(k.created_at).toLocaleDateString('pt-BR')}</span>
                  {!k.used && (
                    <Button size="sm" variant="ghost" className="text-red-500 hover:text-red-700 h-7" onClick={() => handleDeleteKey(k.key)} data-testid={`delete-key-${k.key}`}>
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
        {accessKeys.length === 0 && <p className="text-sm text-slate-400 text-center py-8">Nenhuma chave de acesso criada ainda</p>}
      </div>

      <div className="mt-8 pt-6 border-t border-slate-200">
        <Card className="bg-white border-2 border-amber-300 mb-6" data-testid="virtual-key-generator-admin">
          <CardHeader>
            <CardTitle className="text-lg text-slate-900 flex items-center gap-2"><Key className="h-5 w-5 text-amber-500" /> Gerador de Chaves - Influenciador Virtual</CardTitle>
            <p className="text-xs text-slate-500">Gera chaves com 10% de desconto na primeira mensalidade. Lojistas que ativarem ficam na bolha do Influenciador Virtual.</p>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-3 mb-3">
              <Select value={virtualKeyPlan} onValueChange={setVirtualKeyPlan}>
                <SelectTrigger className="w-48" data-testid="virtual-key-plan-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="premium">Premium - R$ 150</SelectItem>
                  <SelectItem value="platinum">Platinum - R$ 300</SelectItem>
                  <SelectItem value="black">Black - R$ 750</SelectItem>
                </SelectContent>
              </Select>
              <Button onClick={handleGenerateVirtualKey} className="bg-amber-500 hover:bg-amber-600 text-white" data-testid="generate-virtual-key-btn">Gerar Chave Virtual</Button>
            </div>
            {generatedVirtualKey && (
              <div className="bg-amber-50 border border-amber-200 rounded-md p-3 flex items-center justify-between" data-testid="generated-virtual-key">
                <code className="font-mono text-lg font-bold text-slate-900 tracking-wider">{generatedVirtualKey}</code>
                <Button size="sm" variant="ghost" onClick={() => { navigator.clipboard.writeText(generatedVirtualKey); toast.success('Copiada!'); }}
                  className="text-amber-600 hover:text-amber-700" data-testid="copy-virtual-key-btn">Copiar</Button>
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="bg-white border border-indigo-200">
          <CardHeader><CardTitle className="text-lg text-slate-900">Convites para Influenciadores</CardTitle></CardHeader>
          <CardContent>
            <div className="flex items-center gap-3 mb-4">
              <Input value={newInviteName} onChange={e => setNewInviteName(e.target.value)}
                placeholder="Nome do influenciador (opcional)" className="max-w-xs" data-testid="invite-name-input" />
              <Button onClick={handleCreateInvite} className="bg-indigo-600 hover:bg-indigo-700 text-white" data-testid="create-invite-btn">Gerar Convite</Button>
            </div>
            <div className="space-y-2">
              {influencerInvites.map(inv => (
                <div key={inv.key} className={`flex items-center justify-between p-3 rounded-lg border ${inv.used ? 'bg-slate-50 border-slate-200' : 'bg-indigo-50/50 border-indigo-200'}`} data-testid={`invite-${inv.key}`}>
                  <div className="flex items-center gap-3">
                    <code className="font-mono text-sm font-bold text-slate-900 bg-white px-3 py-1 rounded border border-slate-200">{inv.key}</code>
                    {inv.intended_for && <span className="text-sm text-slate-600">para: {inv.intended_for}</span>}
                    {inv.used ? <Badge className="bg-green-100 text-green-700 border-green-200 text-xs">Utilizado{inv.used_by ? ` por ${inv.used_by}` : ''}</Badge> : <Badge className="bg-indigo-100 text-indigo-700 border-indigo-200 text-xs">Disponivel</Badge>}
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-slate-400">{new Date(inv.created_at).toLocaleDateString('pt-BR')}</span>
                    {!inv.used && (
                      <Button size="sm" variant="ghost" className="text-red-500 hover:text-red-700 h-7" onClick={() => handleDeleteInvite(inv.key)} data-testid={`delete-invite-${inv.key}`}>
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    )}
                  </div>
                </div>
              ))}
              {influencerInvites.length === 0 && <p className="text-sm text-slate-400 text-center py-4">Nenhum convite criado ainda</p>}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
